import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import IconArrowLeft from "@/assets/icons/ic-arrow-narrow-left.svg";
import icCalendarDate from "@/assets/icons/ic-calendar.svg";
import icClock from "@/assets/icons/ic-clock.svg";
import icUser02 from "@/assets/icons/ic-user-02.svg";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import {
  formatDate,
  getCategoryType,
  MAIN_CATEGORY_LABELS,
  STATUS_CONFIG_ADMIN,
} from "@/lib/utils";
import {
  AdminAgentDetail,
  AgentVersion,
  AgentVersionDetail,
  getAgentDetail,
  getAgentVersionDetail,
  getAgentVersions,
} from "@/services/manage-agent";

const AgentHistoryPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [agent, setAgent] = useState<AdminAgentDetail | null>(null);
  const [versions, setVersions] = useState<AgentVersion[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string>("");
  const [versionDetail, setVersionDetail] = useState<AgentVersionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const PAGE_SIZE = 10;

  const fetchAgent = useCallback(async () => {
    if (!id) return;
    try {
      const response = await getAgentDetail(id);
      if (response.success) {
        setAgent(response.data);
      }
    } catch {
      toast({ description: "에이전트 정보를 불러오는데 실패했습니다." });
    }
  }, [id]);

  const fetchVersionDetail = useCallback(
    async (versionId: string) => {
      if (!id || !versionId) return;
      try {
        const response = await getAgentVersionDetail(id, versionId);
        if (response.success) {
          setVersionDetail(response.data);
        }
      } catch {
        toast({ description: "버전 상세 정보를 불러오는데 실패했습니다." });
      }
    },
    [id],
  );

  const fetchVersions = useCallback(
    async (pageNum: number, append = false) => {
      if (!id) return;
      try {
        if (append) setLoadingMore(true);
        const response = await getAgentVersions(id, {
          page: pageNum,
          page_size: PAGE_SIZE,
        });
        if (response.success && response.data) {
          const items = response.data.items;
          setVersions((prev) => (append ? [...prev, ...items] : items));
          setTotal(response.data.total);
          if (!append && items.length > 0) {
            const current = items.find((v) => v.is_current);
            const initialId = current?.id ?? items[0].id;
            setSelectedVersionId(initialId);
            fetchVersionDetail(initialId);
          }
        }
      } catch {
        toast({ description: "버전 이력을 불러오는데 실패했습니다." });
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [id, fetchVersionDetail],
  );

  useEffect(() => {
    fetchAgent();
    fetchVersions(1);
  }, [fetchAgent, fetchVersions]);

  const handleSelectVersion = (versionId: string) => {
    setSelectedVersionId(versionId);
    fetchVersionDetail(versionId);
  };

  const hasMore = versions.length < total;

  const handleLoadMore = () => {
    if (loadingMore || !hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchVersions(nextPage, true);
  };

  const selectedVersion = versions.find((v) => v.id === selectedVersionId);
  const displayData = versionDetail ?? agent;

  if (!agent) {
    return null;
  }

  const statusConfig = STATUS_CONFIG_ADMIN[agent.status];
  const statusComplete = agent.status === "completed";

  return (
    <>
      {loading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div className="flex flex-col gap-6" style={{ display: loading ? "none" : "flex" }}>
        <button onClick={() => navigate(-1)} className="w-12 h-12 flex items-center justify-center">
          <img src={IconArrowLeft} alt="Back" />
        </button>

        <div className="flex gap-6">
          {/* Left Column */}
          <div className="flex flex-col gap-6 flex-1 min-w-0">
            {/* Agent Summary Header Card */}
            <div className="bg-white rounded p-5 flex flex-col gap-3 border border-[rgba(0,0,0,0.09)]">
              <div className="flex flex-col gap-1">
                <h1 className="text-[32px] leading-[40px] font-normal text-[#000000]">
                  {agent.name}
                </h1>
                <p className="text-sm text-[#000000]">"{agent.short_description}"</p>
              </div>
              <div className="py-1">
                <div className="h-px bg-[rgba(0,0,0,0.09)]" />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-[#B0B0B0]">에이전트 ID</span>
                  <span className="text-base text-[#000000]">#{agent.id}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-[#B0B0B0]">현재 버전</span>
                  <span className="text-base text-[#000000]">v{agent.current_version_number}</span>
                </div>
                {statusConfig && (
                  <div
                    className={`inline-flex items-center gap-1 h-6 rounded ${statusConfig.bg} ${statusConfig.text} ${statusConfig.padding}`}
                  >
                    <span className="text-sm leading-5 font-normal">{statusConfig.label}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Version Detail Card */}
            {(selectedVersion || versions.length === 0) && (
              <div className="bg-white rounded p-5 flex flex-col gap-6 border border-[rgba(0,0,0,0.09)]">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-bold text-[#000000] flex-1">버전 상세</h2>
                    {selectedVersion && (
                      <div className="flex items-center gap-3">
                        {selectedVersion.version_number > 1 && (
                          <span className="inline-flex items-center h-6 px-2 rounded bg-[#FFAA72] text-sm text-[#FD5108]">
                            수정됨
                          </span>
                        )}
                        {selectedVersion.is_current && (
                          <span className="text-sm text-[#000000]">(현재 버전)</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-12">
                    <div className="flex items-center gap-2">
                      <img src={icUser02} alt="" className="w-5 h-5" />
                      <span className="text-base text-[#000000]">
                        {selectedVersion?.modified_by_name ?? agent.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <img src={icCalendarDate} alt="" className="w-5 h-5" />
                      <span className="text-base text-[#000000]">
                        {formatDate(
                          selectedVersion?.created_at ?? agent.updated_at,
                          "YYYY-MM-DD HH:mm",
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="border border-[rgba(0,0,0,0.09)] rounded p-5 flex flex-col gap-3 h-[430px] overflow-y-auto">
                  <ReadOnlyField label="에이전트 이름" value={displayData?.name ?? ""} />
                  <ReadOnlyField label="공급사" value={displayData?.provider ?? ""} />
                  <ReadOnlyField
                    label="한 줄 소개"
                    value={displayData?.short_description ?? ""}
                    multiline
                  />
                  <ReadOnlyField
                    label="대분류"
                    value={displayData?.main_category ?? ""}
                    multiline
                  />
                  <ReadOnlyField
                    label="상세 기능 카테고리"
                    value={displayData?.categories || ""}
                    multiline
                  />

                  {/* <div className="flex flex-col gap-2">
                  <span className="text-sm text-[#000000]">상세 기능 카테고리</span>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center text-sm leading-5 text-[#202020] whitespace-pre-line">
                      {displayData?.categories}
                    </span>
                  </div>
                </div> */}
                  <ReadOnlyField
                    label="적용 분야"
                    value={displayData?.application_area ?? ""}
                    multiline
                  />
                </div>
              </div>
            )}
          </div>

          {/* Right: Version History Sidebar */}
          <div className="w-[312px] h-[747px] bg-white rounded p-5 flex flex-col gap-3 self-stretch border border-[rgba(0,0,0,0.09)]">
            <div className="flex items-center justify-between">
              <span className="text-lg font-bold text-[#000000]">Version History</span>
              <span className="text-sm text-[#000000]">{total || 1} versions</span>
            </div>

            <div
              className="flex flex-col gap-1 overflow-y-auto"
              onScroll={(e) => {
                const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
                if (scrollHeight - scrollTop <= clientHeight + 50) {
                  handleLoadMore();
                }
              }}
            >
              {versions.length === 0 ? (
                <div className="flex flex-col gap-2 p-4 rounded bg-[#FF80131C]">
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-2">
                      <img src={icClock} alt="" className="w-4 h-4" />
                      <span className="text-sm font-bold text-[#000000]">
                        {formatDate(agent.updated_at, "YYYY-MM-DD")}
                      </span>
                      <span className="text-sm text-[#B0B0B0]">
                        {formatDate(agent.updated_at, "HH:mm")}
                      </span>
                    </div>
                    <span className="text-[14px] text-[#FD5108] font-bold">현재 버전</span>
                  </div>
                  <span className="text-xs text-[#B0B0B0]">by {agent.name}</span>
                </div>
              ) : (
                versions.map((version) => (
                  <button
                    key={version.id}
                    onClick={() => handleSelectVersion(version.id)}
                    className={`flex flex-col gap-2 p-4 rounded text-left transition-colors ${selectedVersionId === version.id ? "bg-[#FBE9D61C]" : ""}`}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-2">
                        <img src={icClock} alt="" className="w-4 h-4" />
                        <span className="text-sm font-bold text-[#000000]">
                          {formatDate(version.created_at, "YYYY-MM-DD")}
                        </span>
                        <span className="text-sm text-[#8D8D8D]">
                          {formatDate(version.created_at, "HH:mm")}
                        </span>
                      </div>
                      {version.is_current && (
                        <span className="text-[14px] text-[#FD5108] font-bold">현재 버전</span>
                      )}
                    </div>
                    <span className="text-xs text-[#B0B0B0]">by {version.modified_by_name}</span>
                  </button>
                ))
              )}
              {loadingMore && (
                <div className="py-3 text-center text-sm text-[#B0B0B0]">로딩 중...</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

function ReadOnlyField({
  label,
  value,
  multiline,
}: {
  label: string;
  value: string;
  multiline?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-[#000000]">{label}</span>
      {multiline ? (
        <Textarea
          readOnly
          value={value}
          className="rounded px-4 py-3 bg-white border border-dashed border-[#00000027] min-h-[88px] text-base font-bold text-[#000000] resize-none focus-visible:ring-0"
        />
      ) : (
        <Input
          readOnly
          value={value}
          className="h-12 px-4 rounded bg-white border border-dashed border-[#00000027] text-base font-bold text-[#000000] focus-visible:ring-0"
        />
      )}
    </div>
  );
}

export default AgentHistoryPage;
